import { useState } from 'react';
import { ChatBubbleLeftRightIcon, ShareIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import type { Address } from '@/models/address';
import { useDocumentosParaMensagem, useModelosMensagem, usePreCadastroAdmissaoQuery } from '../../../hooks/data';
import { escolherModeloMensagem, modeloExtraVale, preencherMensagem } from '../../../models/mensagensContato.modelos';
import type { SituacaoMensagem } from '../../../models/mensagensContato.modelos';
import { mensagensContatoService } from '../../../services/mensagensContatoService';
import { montarLinkTeams, montarLinkWhatsApp, semNegrito, telefoneParaWhatsApp } from '../../../utils/mensagensContato';
import {
  CHAVES_ENDERECO_PRE_CADASTRO,
  CHAVES_TELEFONE_PRE_CADASTRO,
  enderecoParaEntrega,
  formatarEndereco,
  respostaPreCadastro,
} from '../../../utils/enderecoContato';

interface ContatoColaboradorBotoesProps {
  situacao: SituacaoMensagem;
  /** Perfil do colaborador (null na Admissão ainda sem vínculo). */
  colaborador: { name: string; phone?: string | null; email?: string | null; addresses?: Address[] | null } | null;
  nomeColaboradorPendente: string | null;
  /** Só na Admissão: telefone/endereço que a pessoa preencheu no formulário. */
  preCadastroId: string | null;
  mostrarTeams: boolean;
  dataPrevista: string | null;
}

function base64ParaArquivo(base64: string, contentType: string, nome: string): File {
  const binario = atob(base64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
  return new File([bytes], nome, { type: contentType });
}

/**
 * "Chamar no WhatsApp/Teams" com o modelo configurado em Configurações → Mensagens de contato.
 * Endereço: na Admissão, o do formulário (Pré-cadastro); nos demais, o do cadastro. Os links do
 * WhatsApp/Teams não anexam arquivo — os documentos vão como link no texto. O "Compartilhar com
 * anexo" usa o compartilhamento do sistema (celular, Edge/Chrome no Windows) com o arquivo de
 * verdade; a pessoa escolhe o WhatsApp na lista.
 */
export default function ContatoColaboradorBotoes({
  situacao,
  colaborador,
  nomeColaboradorPendente,
  preCadastroId,
  mostrarTeams,
  dataPrevista,
}: ContatoColaboradorBotoesProps) {
  const { user } = useAuth();
  const toast = useToast();
  const { modelos, extras } = useModelosMensagem();
  const { data: documentosPortal = [] } = useDocumentosParaMensagem();
  const { data: preCadastro } = usePreCadastroAdmissaoQuery(preCadastroId);
  const [compartilhando, setCompartilhando] = useState(false);
  // '' = a automática (escolhida pela situação); senão, o id de um modelo criado pelo SI.
  const [modeloEscolhidoId, setModeloEscolhidoId] = useState('');

  const automatico = modelos.find((m) => m.id === escolherModeloMensagem(situacao))!;
  const extrasDoFluxo = extras.filter((m) => modeloExtraVale(m.fluxo, situacao.tipoFluxo));
  const modelo = extrasDoFluxo.find((m) => m.id === modeloEscolhidoId) ?? automatico;
  const documentos = documentosPortal.filter((d) => modelo.documentoIds.includes(d.id));
  const endereco =
    respostaPreCadastro(preCadastro, CHAVES_ENDERECO_PRE_CADASTRO) ??
    formatarEndereco(enderecoParaEntrega(colaborador?.addresses));
  const telefone =
    colaborador?.phone || telefoneParaWhatsApp(respostaPreCadastro(preCadastro, CHAVES_TELEFONE_PRE_CADASTRO));
  const valores = {
    nome: colaborador?.name ?? nomeColaboradorPendente ?? '',
    remetente: (user?.displayName ?? '').trim().split(' ')[0] || 'Time de SI',
    codigoRastreio: situacao.codigoRastreio,
    dataPrevista,
    endereco,
  };
  const mensagem = preencherMensagem(
    modelo.texto,
    valores,
    documentos.map((d) => ({ titulo: d.titulo, url: d.url }))
  );
  const linkWhatsApp = telefone ? montarLinkWhatsApp(telefone, mensagem) : null;
  // Teams não formata o *negrito* do WhatsApp no texto pré-preenchido — vai sem os asteriscos.
  const linkTeams =
    mostrarTeams && colaborador?.email ? montarLinkTeams(colaborador.email, semNegrito(mensagem)) : null;
  const comArquivo = documentos.filter((d) => d.path);
  const podeCompartilhar = comArquivo.length > 0 && typeof navigator !== 'undefined' && 'canShare' in navigator;
  const faltaEndereco = !endereco && modelo.texto.includes('{endereco}');

  const compartilharComAnexo = async () => {
    setCompartilhando(true);
    try {
      const arquivos = await Promise.all(
        comArquivo.map(async (d) => {
          const { base64, contentType } = await mensagensContatoService.baixarArquivo(d.path!);
          return base64ParaArquivo(base64, contentType || d.contentType || 'application/pdf', d.nomeArquivo);
        })
      );
      if (!navigator.canShare({ files: arquivos })) {
        toast.error('Este navegador não compartilha arquivos. Use "Chamar no WhatsApp" (o documento vai como link).');
        return;
      }
      // Com anexo, o texto vai sem os links (o arquivo já está indo junto).
      await navigator.share({ files: arquivos, text: preencherMensagem(modelo.texto, valores, []) });
    } catch (erro) {
      if ((erro as Error)?.name !== 'AbortError') {
        toast.error('Não foi possível compartilhar com anexo. Use "Chamar no WhatsApp" (o documento vai como link).');
      }
    } finally {
      setCompartilhando(false);
    }
  };

  if (!linkWhatsApp && !linkTeams) return null;

  return (
    <div className="mt-3 space-y-2">
      {extrasDoFluxo.length > 0 && (
        <label className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          Mensagem:
          <select
            className="input-primary py-1 text-xs"
            value={modeloEscolhidoId}
            onChange={(event) => setModeloEscolhidoId(event.target.value)}
          >
            <option value="">{automatico.titulo} (automática)</option>
            {extrasDoFluxo.map((m) => (
              <option key={m.id} value={m.id}>
                {m.titulo}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="flex flex-wrap gap-2">
        {linkWhatsApp && (
          <a
            href={linkWhatsApp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-1.5 text-sm"
            title="Abre o WhatsApp com a mensagem já preenchida (documentos vão como link) — ainda precisa clicar em enviar"
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4" />
            Chamar no WhatsApp
          </a>
        )}
        {linkTeams && (
          <a
            href={linkTeams}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-1.5 text-sm"
            title="Abre o Teams com a mensagem já preenchida (documentos vão como link) — ainda precisa clicar em enviar"
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4" />
            Chamar no Teams
          </a>
        )}
        {podeCompartilhar && (
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1.5 text-sm"
            disabled={compartilhando}
            onClick={compartilharComAnexo}
            title="Abre o compartilhamento do sistema com o arquivo anexado — escolha o WhatsApp na lista"
          >
            <ShareIcon className="h-4 w-4" />
            {compartilhando ? 'Preparando anexo...' : 'Compartilhar com anexo'}
          </button>
        )}
      </div>
      {documentos.length > 0 && (
        <p className="text-xs text-gray-500">Vai junto: {documentos.map((d) => `📎 ${d.titulo}`).join(' · ')}</p>
      )}
      {faltaEndereco && (
        <p className="text-xs text-amber-700">
          Endereço não encontrado {preCadastroId ? 'no formulário nem no cadastro' : 'no cadastro'} — a mensagem vai
          pedindo o endereço completo.
        </p>
      )}
    </div>
  );
}
